import { Pill, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FeatureFlagVariants } from "@src/appV2/FeatureFlags/types";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { ChatActionCard } from "@src/appV2/redesign/Chat/ChatActionCard";
import { ChatWidgetWrapper } from "@src/appV2/redesign/Chat/ChatWidgetWrapper";
import { useWorkerEtaImprovementsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useWorkerEtaImprovementsConfig";
import { Button } from "@src/appV2/redesign/components/Button";
import { UpdateEtaBottomSheetContainer } from "@src/appV2/redesign/Shift/WorkerEta/UpdateEta/Container";
import { UPDATE_ETA_BOTTOM_SHEET_SOURCE } from "@src/appV2/redesign/Shift/WorkerEta/UpdateEta/types";
import { UpdateEtaBottomSheet } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/UpdateEtaBottomSheet";
import { useCheckAttendanceV1 } from "@src/appV2/Shifts/UrgentShifts/api/useCheckAttendance.v1";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { Shift as OldShift } from "@src/lib/interface/src";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

interface InputEtaMessageProps {
  shift: OldShift;
  urgentShiftsConfig: FeatureFlagVariants[CbhFeatureFlag.URGENT_SHIFTS_CONFIG];
}

function InputEtaMessage(props: InputEtaMessageProps) {
  const { shift, urgentShiftsConfig } = props;
  const worker = useDefinedWorker();
  const modalState = useModalState();
  const { enabled: isWorkerEtaImprovementsEnabled } = useWorkerEtaImprovementsConfig();
  const maxEtaBoundInMinutes = urgentShiftsConfig?.checkAttendanceBoundsInMinutes?.[1];
  const { data, isError, isLoading } = useCheckAttendanceV1({
    urgentShiftsServiceUrl: environmentConfig.REACT_APP_URGENT_SHIFTS_SERVICE_URL,
    shiftId: shift._id ?? "",
    shiftStart: shift.start ?? "",
    options: {
      cacheTime: 0,
    },
  });

  if (isLoading) {
    return null;
  }

  if (isError || !isDefined(shift._id) || !isDefined(shift.start)) {
    logError(APP_V2_APP_EVENTS.INPUT_ETA_MESSAGE_RENDER_FAILURE, {
      error: new Error("Failed to render input eta message"),
      metadata: {
        shiftId: shift._id,
        shiftStart: shift.start,
      },
    });
    return null;
  }

  if (shift.deleted || !data.attendanceCheck) {
    return null;
  }

  const shiftStart = formatInTimeZone(shift.start, worker?.tmz ?? "UTC", "MM/dd");
  const shiftTime = formatInTimeZone(shift.start, worker?.tmz ?? "UTC", "h:mm a");

  const systemMessage = `Running late for your ${shiftStart} 
                ${shiftTime} shift? Tap to update
                the facility with your ETA`;

  return (
    <ChatWidgetWrapper>
      <ChatActionCard
        message={systemMessage}
        pill={
          <Pill
            variant="outlined"
            size="small"
            label="Update ETA"
            backgroundColor={(theme) => theme.palette.background.quaternary}
            color={(theme) => theme.palette.intent?.warning.text}
            outlineColor={(theme) => theme.palette.intent?.warning.border}
            sx={{
              width: "fit-content",
            }}
          />
        }
      >
        <Button fullWidth variant="contained" size="small" onClick={modalState.openModal}>
          Update ETA
        </Button>
      </ChatActionCard>

      {/* FIXME: Mismatch between shift from legacy and appV2 defined Shift objects.
            Change the usages of this component to use appV2 Shift Object. */}
      {maxEtaBoundInMinutes &&
        (isWorkerEtaImprovementsEnabled ? (
          <UpdateEtaBottomSheetContainer
            shiftId={shift._id}
            modalState={modalState}
            source={UPDATE_ETA_BOTTOM_SHEET_SOURCE.WORKPLACE_CHAT}
          />
        ) : (
          <UpdateEtaBottomSheet
            shiftId={shift._id}
            modalState={modalState}
            source={UPDATE_ETA_BOTTOM_SHEET_SOURCE.WORKPLACE_CHAT}
          />
        ))}
    </ChatWidgetWrapper>
  );
}

export default InputEtaMessage;
